import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  MessageCircle,
  ChevronRight,
  Check,
  TrendingDown,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSeo from '../hooks/useSeo';
const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
const SITE_URL =
  import.meta.env.VITE_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const formatPrice = (min, max, currency = 'GBP') => {
  if (min == null && max == null) return null;
  const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
  const fmt = (n) =>
    `${symbol}${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  if (min != null && max != null && Number(min) === Number(max)) return fmt(min);
  if (min != null && max != null) return `${fmt(min)} - ${fmt(max)}`;
  return fmt(min ?? max);
};

const PriceComparison = ({ service, t }) => {
  const ukPrice = formatPrice(service.price_uk_min, service.price_uk_max, service.price_currency || 'GBP');
  const trPrice = formatPrice(service.price_tr_min, service.price_tr_max, service.price_currency || 'GBP');
  if (!ukPrice && !trPrice) return null;

  return (
    <div className="mt-8 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
          <TrendingDown size={20} />
        </span>
        <h3 className="text-lg font-bold text-[#064e3b]">
          {t('services.price_comparison', 'UK vs Turkey — Price Comparison')}
        </h3>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="rounded-xl bg-white p-4 border border-slate-100">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">
            {t('services.uk_price', 'UK Price')}
          </p>
          <p className="text-2xl font-bold text-slate-700 line-through opacity-80">{ukPrice || '—'}</p>
        </div>
        <div className="rounded-xl bg-emerald-600 text-white p-4 shadow-md">
          <p className="text-xs uppercase tracking-wider text-emerald-100 mb-1">
            {t('services.tr_price', 'Crystal Dental (Turkey)')}
          </p>
          <p className="text-2xl font-bold">{trPrice || '—'}</p>
        </div>
        {service.savings_percent ? (
          <div className="rounded-xl bg-white p-4 border border-emerald-200">
            <p className="text-xs uppercase tracking-wider text-emerald-600 mb-1">
              {t('services.you_save', 'You Save')}
            </p>
            <p className="text-2xl font-bold text-emerald-700">{service.savings_percent}%</p>
          </div>
        ) : null}
      </div>
      <p className="mt-3 text-xs text-slate-500">
        {t(
          'services.price_disclaimer',
          'Prices are indicative and may vary based on individual diagnosis. Final pricing is provided after free consultation.'
        )}
      </p>
    </div>
  );
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const { currentLanguage, t } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSeo(
    'services',
    currentLanguage,
    service
      ? {
          title: service.meta_title || `${service.title} | Crystal Dental Clinic`,
          description: service.meta_description || service.short_description || '',
          image: service.image_url || '',
          canonical: `${SITE_URL}/services/${service.slug}`,
          path: `/services/${service.slug}`,
          hreflangPath: `/services/${service.slug}`,
        }
      : null
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await axios.get(`${API_URL}/api/services/${slug}`, {
          params: { lang: currentLanguage },
        });
        if (cancelled) return;
        setService(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 404) setNotFound(true);
          setService(null);
        }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug, currentLanguage]);

  if (loading) {
    return (
      <div className="pt-12 pb-16 container-custom px-4">
        <div className="glass-card p-8 text-[#64748b]">Loading…</div>
      </div>
    );
  }

  if (notFound || !service) {
    return (
      <div className="pt-12 pb-16 container-custom px-4">
        <div className="glass-card p-8 text-center">
          <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Service not found</h1>
          <p className="text-[#475569] mb-6">
            The service you're looking for doesn't exist or has been moved.
          </p>
          <Link to="/services" className="inline-flex items-center gap-2 text-sky-700 font-semibold">
            <ArrowLeft size={16} /> Back to all services
          </Link>
        </div>
      </div>
    );
  }

  const paragraphs = (service.full_description || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const treatments = Array.isArray(service.treatments) ? service.treatments : [];
  const siblings = Array.isArray(service.siblings) ? service.siblings : [];
  const isCategory = service.parent_id == null;

  return (
    <div className="pt-12 pb-16 container-custom px-4 space-y-8">
      <nav className="text-sm text-[#64748b] flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:text-sky-700">
          Home
        </Link>
        <ChevronRight size={14} />
        <Link to="/services" className="hover:text-sky-700">
          Services
        </Link>
        {service.parent ? (
          <>
            <ChevronRight size={14} />
            <Link to={`/services/${service.parent.slug}`} className="hover:text-sky-700">
              {service.parent.title || service.parent.slug}
            </Link>
          </>
        ) : null}
        <ChevronRight size={14} />
        <span className="text-[#0f172a] font-medium truncate">{service.title || service.slug}</span>
      </nav>

      <article className="glass-card overflow-hidden p-0">
        {service.image_url ? (
          <div className="aspect-[21/9] max-h-[460px] overflow-hidden">
            <img
              src={service.image_url}
              alt={service.title || service.slug}
              className="w-full h-full object-cover"
            />
          </div>
        ) : null}
        <div className="p-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">
            {service.title || service.slug}
          </h1>
          {service.short_description ? (
            <p className="text-sky-700 text-lg font-semibold mb-6">{service.short_description}</p>
          ) : null}

          {paragraphs.length > 0 ? (
            <div className="prose-like space-y-4 text-[#475569] leading-8 text-base md:text-lg">
              {paragraphs.map((p, idx) => (
                <p key={idx} className="whitespace-pre-line">
                  {p}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-[#64748b]">
              Detailed information about this service is being prepared. Please contact us for any
              questions.
            </p>
          )}

          <PriceComparison service={service} t={t} />

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/appointment${service?.id ? `?service=${service.id}` : ''}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-sky-600 to-sky-500 text-white font-semibold shadow-lg hover:shadow-xl transition-shadow"
            >
              <CalendarCheck size={18} />
              {t('hero.cta_appointment', 'Book Appointment')}
            </Link>
            <a
              href="https://wa.me/905495205000"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-emerald-200 text-emerald-700 bg-emerald-50 font-semibold hover:bg-emerald-100"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </article>

      {/* Treatments under this category */}
      {isCategory && treatments.length > 0 && (
        <section className="glass-card p-8 space-y-5">
          <header>
            <h2 className="text-xl md:text-2xl font-bold text-[#0f172a]">
              {t('services.treatments_in_category', 'Treatments in this category')}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {t(
                'services.treatments_intro',
                'Click any treatment to read details and see UK vs Turkey pricing.'
              )}
            </p>
          </header>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {treatments.map((tr) => {
              const tp = formatPrice(tr.price_tr_min, tr.price_tr_max, tr.price_currency || 'GBP');
              const up = formatPrice(tr.price_uk_min, tr.price_uk_max, tr.price_currency || 'GBP');
              return (
                <Link
                  key={tr.id}
                  to={`/services/${tr.slug}`}
                  className="group block p-4 rounded-xl border border-slate-100 hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-7 h-7 rounded-full bg-sky-100 text-sky-700 grid place-items-center group-hover:bg-sky-600 group-hover:text-white transition-colors">
                      <Check size={14} />
                    </span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#0f172a] group-hover:text-sky-700">
                        {tr.title || tr.slug}
                      </h3>
                      {tr.short_description ? (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {tr.short_description}
                        </p>
                      ) : null}
                      {(tp || up || tr.savings_percent) && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                          {up ? (
                            <span className="text-slate-400 line-through">{up}</span>
                          ) : null}
                          {tp ? (
                            <span className="font-bold text-emerald-700">{tp}</span>
                          ) : null}
                          {tr.savings_percent ? (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                              -{tr.savings_percent}%
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Sibling treatments under same parent */}
      {!isCategory && siblings.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl md:text-2xl font-bold text-[#0f172a]">
              {t('services.related_treatments', 'Related Treatments')}
              {service.parent ? (
                <span className="text-slate-400 text-base font-normal">
                  {' '}
                  · {service.parent.title}
                </span>
              ) : null}
            </h2>
            {service.parent ? (
              <Link
                to={`/services/${service.parent.slug}`}
                className="text-sky-700 font-semibold inline-flex items-center gap-1"
              >
                {t('common.view_all', 'View All')} <ArrowRight size={16} />
              </Link>
            ) : null}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {siblings.map((tr) => (
              <Link
                key={tr.id}
                to={`/services/${tr.slug}`}
                className="block p-4 rounded-xl border border-slate-100 hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
              >
                <h3 className="font-semibold text-[#0f172a]">{tr.title || tr.slug}</h3>
                {tr.short_description ? (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{tr.short_description}</p>
                ) : null}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ServiceDetail;
